import { useEffect, useState } from 'react';
import { readPrerenderData, publishPrerenderData } from '../utils/prerenderData';

/**
 * A value the prerenderer computed, replayed for the client's FIRST render.
 *
 * WHY THIS EXISTS
 *
 * Anything a component derives from the clock is a value the capture had and
 * the visitor's first render does not:
 *
 *     capture (build machine, Thu, IST)   "Reflection fuels growth."
 *     visitor (Sunday, or simply in UTC)  "Weekend energy: dream bigger..."
 *
 * React finds different text and answers by discarding the subtree (#425), and
 * hydration for the whole page unravels from there.
 *
 * SEEDING THE TIMESTAMP IS NOT ENOUGH, which is the part that took a while to
 * see. getHours() and getDay() read LOCAL time, so two people holding the same
 * instant still disagree: capturedAt 1785962192409 is day 4 in IST and day 3 in
 * UTC. A shared `now` fixes the clock and leaves the timezone. What has to
 * travel is the rendered OUTCOME, not the inputs it was derived from.
 *
 * So: the prerenderer records what it rendered, the first client render replays
 * exactly that and therefore matches the file, and the effect immediately
 * recomputes from the visitor's real clock and zone. A visitor sees their own
 * correct greeting one frame later, and hydration never sees the difference.
 *
 * On a page with no seed (dev, or a route the prerenderer does not build) both
 * passes compute normally, which is the behaviour that was there before.
 *
 * WHERE THE PUBLISH MUST LIVE: in an effect, on the value. src/utils/
 * prerenderData.js documents what happens otherwise; publishing inside a state
 * updater never reached the saved file at all, and publishing at the call sites
 * that set it recorded a stale value.
 *
 * @param {string} key   name in the prerender bag; must be unique per value
 * @param {() => any} compute  derives the live value; must be JSON-serialisable
 */
export function useSeededValue(key, compute) {
  const [value, setValue] = useState(() => {
    const seeded = readPrerenderData(key);
    return seeded !== undefined && seeded !== null ? seeded : compute();
  });

  useEffect(() => {
    const fresh = compute();
    publishPrerenderData(key, fresh);
    // Compare by content, not identity. compute() usually returns a fresh
    // object ({text, emoji}), so an unguarded setValue would re-render on every
    // mount and hand every consumer a new identity for an unchanged value.
    setValue((prev) => (JSON.stringify(prev) === JSON.stringify(fresh) ? prev : fresh));
    // Deliberately mount-only: the point is to correct the seed once, after the
    // pass hydration compares. `compute` is not in the deps because callers
    // routinely pass an inline arrow, and depending on it would re-run forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return value;
}

export default useSeededValue;
